import { useAuiState } from "@assistant-ui/react";
import { type RefObject, useCallback, useEffect, useState } from "react";
import { CopilotComposer } from "@/features/copilot/components/copilot-composer";
import { CopilotThread } from "@/features/copilot/components/copilot-thread";
import { copilotBlockingNotice } from "@/features/copilot/lib/copilot-action-blocking";
import type { CopilotConversation } from "@/features/copilot/schemas/copilot.schema";

export function CopilotConversationBody({
  conversation,
  composerRef,
  noCredits,
  onRuntimeRunningChange,
}: {
  conversation: CopilotConversation | null;
  composerRef: RefObject<HTMLTextAreaElement | null>;
  noCredits: boolean;
  onRuntimeRunningChange: (running: boolean) => void;
}) {
  const runtimeThreadId = useAuiState((state) => state.threads.mainThreadId);
  const runtimeIsLoading = useAuiState((state) => state.thread.isLoading);
  const [serverIsRunning, setServerIsRunning] = useState(
    conversation?.activeTurn?.status === "running",
  );

  useEffect(() => {
    setServerIsRunning(conversation?.activeTurn?.status === "running");
  }, [conversation?.activeTurn?.status]);

  const onRunActivity = useCallback(
    (running: boolean) => {
      onRuntimeRunningChange(running);
      if (!running) setServerIsRunning(false);
    },
    [onRuntimeRunningChange],
  );

  if (!conversation) return null;
  if (runtimeThreadId !== conversation.id || runtimeIsLoading) {
    return (
      <div
        className="flex min-h-0 flex-1 items-center justify-center text-sm text-text-secondary"
        role="status"
        aria-label="Loading Hand conversation"
      >
        Loading conversation…
      </div>
    );
  }

  return (
    <>
      <CopilotThread conversation={conversation} onRunActivity={onRunActivity} />
      <CopilotComposer
        noCredits={noCredits}
        serverIsRunning={serverIsRunning}
        serverTurnStatus={conversation.activeTurn?.status}
        blockingNotice={copilotBlockingNotice(conversation.actions)}
        composerRef={composerRef}
      />
    </>
  );
}
